import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Trash2, X } from 'lucide-react';

import { usePedestrianLayerTypeEnabled } from '../../hooks/useIdlc1129Features';
import { layerTypeKeys, layerTypeLabel } from '../../utils/layerTypeLabel';
import { LAYER_TYPES } from '../project-settings/types';
import { CadPolygon } from './types';

type LayerType = keyof typeof LAYER_TYPES;

interface PolygonSelectionBarProps {
  selected: CadPolygon[];
  onClear: () => void;
  /** The same change to every selected polygon (one undo step). */
  onUpdate: (updates: Partial<CadPolygon>) => void;
  onDelete: () => void;
}

/** The value all selected polygons share, or undefined when they differ. */
function shared<T>(polygons: CadPolygon[], value: (p: CadPolygon) => T): T | undefined {
  const first = value(polygons[0]);
  return polygons.every((p) => value(p) === first) ? first : undefined;
}

const field = 'h-7 rounded border border-white/10 bg-black px-2 text-xs text-white outline-none focus:border-lime-400';

/**
 * Edits the polygons ticked in the list together: type and height, or deletes them (lighting is
 * set up in the project's workspace). A value the polygons do not share shows as "mixed".
 */
export function PolygonSelectionBar({ selected, onClear, onUpdate, onDelete }: PolygonSelectionBarProps) {
  const { t } = useTranslation();
  const pedestrianLayerType = usePedestrianLayerTypeEnabled();
  const [height, setHeight] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const mixed = t('projects.cadProject.selection.mixed', 'Mixed');

  const type = shared(selected, (p) => p.type);
  const sharedHeight = shared(selected, (p) => p.height);

  const applyHeight = () => {
    const value = parseFloat(height.replace(',', '.'));
    if (!Number.isFinite(value) || value < 0) return;
    onUpdate({ height: value });
    setHeight('');
  };

  return (
    <div className="sticky top-[33px] z-[2] border-b border-lime-400/30 bg-[#1b2112] px-4 py-3 space-y-2.5">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-lime-400">
          {t('projects.cadProject.selection.count', { count: selected.length, defaultValue: '{{count}} selected' })}
        </span>
        <button
          type="button"
          onClick={onClear}
          className="flex items-center gap-1 text-[11px] text-gray-400 hover:text-white"
          aria-label={t('projects.cadProject.selection.clear', 'Clear selection')}
        >
          <X className="h-3.5 w-3.5" />
          {t('projects.cadProject.selection.clear', 'Clear selection')}
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <label className="flex items-center gap-2">
          <span className="text-[11px] text-gray-400">{t('workspace.type')}</span>
          <select
            className={field}
            value={type ?? ''}
            onChange={(e) => e.target.value && onUpdate({ type: e.target.value as LayerType })}
          >
            {type === undefined && <option value="">{mixed}</option>}
            {layerTypeKeys(pedestrianLayerType, type).map((key) => (
              <option key={key} value={key}>
                {layerTypeLabel(t, key)}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2">
          <span className="text-[11px] text-gray-400">H (m)</span>
          <input
            type="number"
            min={0}
            step={0.5}
            value={height}
            placeholder={sharedHeight !== undefined ? String(sharedHeight) : mixed}
            onChange={(e) => setHeight(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && applyHeight()}
            className={`${field} w-16 text-right tabular-nums placeholder:text-gray-500`}
          />
          <button
            type="button"
            onClick={applyHeight}
            disabled={!height}
            className="h-7 rounded border border-white/20 px-2 text-xs text-white hover:border-lime-400 disabled:opacity-40"
          >
            {t('projects.cadProject.selection.set', 'Set')}
          </button>
        </label>
      </div>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => (confirmDelete ? onDelete() : setConfirmDelete(true))}
          onBlur={() => setConfirmDelete(false)}
          className={`flex h-7 items-center gap-1.5 rounded px-2.5 text-xs transition-colors ${
            confirmDelete ? 'bg-red-500 text-white' : 'text-red-400 hover:bg-red-400/15'
          }`}
        >
          <Trash2 className="h-3.5 w-3.5" />
          {confirmDelete
            ? t('projects.cadProject.selection.confirmDelete', {
                count: selected.length,
                defaultValue: 'Delete {{count}}?',
              })
            : t('projects.cadProject.selection.delete', { count: selected.length, defaultValue: 'Delete ({{count}})' })}
        </button>
      </div>
    </div>
  );
}
